/**
 * Enterprise-grade entity fetching hook
 * 
 * Provides:
 * - Single entity fetching with loading/error states
 * - Automatic refetch on ID change
 * - Error handling with user-friendly messages
 * - Retry logic support
 */

import { useState, useEffect, useCallback, useRef } from 'react';
import { useToast } from './useToast';
import { handleError, shouldShowError, type ErrorContext } from '@/utils/errorHandler';
import { logger } from '@/utils/logger';

export interface UseEntityOptions<T> {
  fetchFn: (id: string) => Promise<T>;
  entityId: string | null | undefined;
  entityName?: string;
  enabled?: boolean;
  onSuccess?: (data: T) => void;
  onError?: (error: unknown) => void;
}

export interface UseEntityResult<T> {
  data: T | null;
  isLoading: boolean;
  error: unknown | null;
  refetch: () => Promise<void>;
}

/**
 * Hook for fetching a single entity
 */
export function useEntity<T>(
  options: UseEntityOptions<T>
): UseEntityResult<T> {
  const {
    fetchFn,
    entityId,
    entityName = 'item',
    enabled = true,
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState<T | null>(null);
  // Initialize loading as true when entityId is provided and enabled to prevent flickering
  // This ensures LoadingState is shown immediately instead of ErrorState
  const [isLoading, setIsLoading] = useState(() => !!(entityId && enabled));
  const [error, setError] = useState<unknown | null>(null);

  const { toast } = useToast();
  const abortControllerRef = useRef<AbortController | null>(null);

  // Track if we're currently fetching to prevent duplicate requests
  const isFetchingRef = useRef(false);
  
  // Track the last fetched entityId to prevent duplicate fetches
  const lastFetchedIdRef = useRef<string | null>(null);
  
  // Store callbacks in refs to avoid dependency issues that cause flickering
  const onSuccessRef = useRef(onSuccess);
  const onErrorRef = useRef(onError);
  const toastRef = useRef(toast);
  
  // Update refs when callbacks change (but don't trigger re-render)
  useEffect(() => {
    onSuccessRef.current = onSuccess;
    onErrorRef.current = onError;
    toastRef.current = toast;
  }, [onSuccess, onError, toast]);

  const fetchEntity = useCallback(async () => {
    // If disabled or no entityId, reset loading state and don't fetch
    if (!enabled || !entityId) {
      setIsLoading(false);
      setData(null);
      setError(null);
      isFetchingRef.current = false;
      return;
    }

    // Prevent duplicate simultaneous requests
    if (isFetchingRef.current) {
      console.log(`[useEntity] Already fetching ${entityName}, skipping duplicate request`);
      return;
    }

    // Cancel previous request
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }

    // Create new abort controller
    abortControllerRef.current = new AbortController();
    isFetchingRef.current = true;

    // Set a timeout to prevent infinite loading (max 120 seconds - increased as requested)
    const timeoutId = setTimeout(() => {
      if (isFetchingRef.current) {
        console.error(`[useEntity] Fetch timeout for ${entityName}:`, entityId);
        if (abortControllerRef.current) {
          abortControllerRef.current.abort();
        }
        setError(new Error(`Request timeout: ${entityName} fetch took too long`));
        setIsLoading(false);
        isFetchingRef.current = false;
      }
    }, 120000); // Increased to 120 seconds as requested

    try {
      setIsLoading(true);
      setError(null);

      console.log(`[useEntity] Starting fetch for ${entityName}:`, entityId);

      const entity = await fetchFn(entityId);

      // Check if request was aborted
      // However, if we successfully received data, use it even if abort was triggered
      // (This handles race conditions where abort is called after fetch completes)
      if (abortControllerRef.current.signal.aborted) {
        // If we have data, use it (fetch already completed successfully)
        if (entity != null) {
          console.log(`[useEntity] Request marked as aborted but data received for ${entityName}, using data:`, entityId);
          // Continue to set data below
        } else {
          console.log(`[useEntity] Request aborted for ${entityName}:`, entityId);
          return;
        }
      }

      console.log(`[useEntity] Successfully fetched ${entityName}:`, entity);

      // Update last fetched ID to prevent duplicate fetches
      lastFetchedIdRef.current = entityId;
      setData(entity);

      // Log success
      logger.debug(`Fetched ${entityName}`, {
        entityId,
      });

      // Call success callback AFTER state is set (use setTimeout to avoid blocking)
      // Use ref to avoid dependency issues
      if (onSuccessRef.current) {
        // Use setTimeout to ensure state update completes before callback
        setTimeout(() => {
          onSuccessRef.current?.(entity);
        }, 0);
      }
    } catch (err: unknown) {
      // Log error details for debugging first
      console.error(`[useEntity] Error fetching ${entityName}:`, {
        entityId,
        error: err,
        errorType: err instanceof Error ? err.constructor.name : typeof err,
        errorMessage: err instanceof Error ? err.message : String(err),
        aborted: abortControllerRef.current?.signal.aborted || false,
      });

      // Check if request was aborted - if so, don't process error, just clean up
      if (abortControllerRef.current?.signal.aborted) {
        console.log(`[useEntity] Request aborted, skipping error handling for ${entityName}:`, entityId);
        // Still need to clear state and call finally block
        setData(null);
        setError(null);
        isFetchingRef.current = false;
        setIsLoading(false);
        return;
      }

      // Process error normally if not aborted
      const errorContext: ErrorContext = {
        operation: 'fetch',
        entity: entityName,
        entityId,
      };

      const handledError = handleError(err, errorContext);

      // Set error state BEFORE showing toast to ensure component re-renders
      setData(null);
      setError(err);
      setIsLoading(false); // Set loading to false immediately in catch block

      // Call error callback FIRST (before toast to prevent rapid re-renders)
      // Use ref to avoid dependency issues
      if (onErrorRef.current) {
        // Use setTimeout to ensure state update completes before callback
        setTimeout(() => {
          onErrorRef.current?.(err);
        }, 0);
      }

      // Show error toast AFTER a delay to prevent flickering
      if (shouldShowError(handledError, errorContext)) {
        setTimeout(() => {
          toastRef.current({
            variant: 'destructive',
            title: 'Error',
            description: handledError.userMessage,
          });
        }, 500); // Increased delay to prevent flickering
      }
    } finally {
      clearTimeout(timeoutId);
      const wasAborted = abortControllerRef.current?.signal.aborted || false;
      console.log(`[useEntity] Fetch completed for ${entityName}:`, {
        entityId,
        aborted: wasAborted,
        isLoadingState: 'will be set to false',
      });
      isFetchingRef.current = false;
      // Always set loading to false in finally block as a safety net
      // This prevents infinite loading state even if catch block doesn't run
      setIsLoading(false);
    }
  }, [fetchFn, entityId, enabled, entityName, onSuccessRef, onErrorRef, toastRef]); // Include stable dependencies - callbacks stored in refs are updated separately

  // Fetch entity when ID changes or enabled becomes true
  useEffect(() => {
    // Reset state when disabled or no entityId
    if (!enabled || !entityId) {
      setIsLoading(false);
      setData(null);
      setError(null);
      lastFetchedIdRef.current = null;
      // Abort any ongoing request
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
      isFetchingRef.current = false;
      return;
    }

    // Prevent duplicate fetches for the same entityId (only if we already have data)
    if (lastFetchedIdRef.current === entityId && data !== null) {
      console.log(`[useEntity] Skipping duplicate fetch for ${entityName} with ID:`, entityId);
      return;
    }

    // Reset last fetched ID when entityId changes
    if (lastFetchedIdRef.current !== entityId) {
      lastFetchedIdRef.current = null;
      setError(null); // Clear error when entityId changes
      setIsLoading(true); // Show loading state immediately when entityId changes
    }

    // Only fetch if enabled and entityId exists and we haven't fetched it yet
    // Add small debounce delay to prevent duplicate rapid requests
    const timeoutId = setTimeout(() => {
      // Double-check conditions after delay to prevent race conditions
      // Only fetch if we haven't already fetched this entityId
      if (enabled && entityId && !isFetchingRef.current && lastFetchedIdRef.current !== entityId) {
        fetchEntity();
      }
    }, 50); // 50ms debounce - just enough to batch rapid re-renders

    // Cleanup: abort request on unmount or when dependencies change
    return () => {
      clearTimeout(timeoutId);
      if (abortControllerRef.current) {
        abortControllerRef.current.abort();
      }
    };
  }, [entityId, enabled, fetchEntity, entityName, data]); // Include all dependencies - data is used to prevent duplicate fetches

  const refetch = useCallback(async () => {
    await fetchEntity();
  }, [fetchEntity]);

  return {
    data,
    isLoading,
    error,
    refetch,
  };
}







